/**
 * NumericTextField Component
 *
 * A numeric input field using React Aria with:
 * - Right-aligned text
 * - Currency sign or text on left side
 * - Number formatting support
 * - Validation
 * - Full accessibility
 */

import React, { useRef } from "react";
import { useTextField, useButton } from "react-aria";
import type { AriaTextFieldProps } from "react-aria";
import styles from "./NumericTextField.module.css";
import "./NumericTextField.responsive.css";

// Helper function to format numbers
function formatNumber(
  value: number,
  decimals: number,
  thousandsSeparator: boolean,
): string {
  const fixed = value.toFixed(decimals);

  if (thousandsSeparator) {
    const parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }

  return fixed;
}

export interface NumericTextFieldProps extends Omit<
  AriaTextFieldProps,
  "type"
> {
  /** Currency symbol or prefix text (e.g., "$", "USD", "€") */
  prefix?: string;
  /** Suffix text (e.g., "%", "kg") */
  suffix?: string;
  /** Number of decimal places */
  decimals?: number;
  /** Enable thousands separator */
  thousandsSeparator?: boolean;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Step increment/decrement value */
  step?: number;
  /** Error message to display */
  errorMessage?: string;
  /** Additional CSS class for container */
  className?: string;
  /** Show increment/decrement buttons */
  showButtons?: boolean;
  width?:
    | string
    | number
    | {
        base?: string | number;
        S?: string | number;
        M?: string | number;
        L?: string | number;
        XL?: string | number;
      };
  /** Callback when value changes (as number) */
  onValueChange?: (value: number | null) => void;
}

export function NumericTextField(props: NumericTextFieldProps) {
  const {
    prefix,
    suffix,
    decimals = 2,
    thousandsSeparator = true,
    min,
    max,
    step = 1,
    errorMessage,
    className = "",
    showButtons = false,
    onValueChange,
    label,
    description,
    isDisabled,
    isRequired,
    value,
    defaultValue,
    width,
    onChange,
    ...otherProps
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const incrementRef = useRef<HTMLButtonElement>(null);
  const decrementRef = useRef<HTMLButtonElement>(null);

  // Track if user is currently editing to avoid formatting during typing
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState("");

  // Format the incoming value for display (only when not editing)
  const displayValue = React.useMemo(() => {
    if (isEditing) {
      return editingValue;
    }

    if (value === undefined || value === null || value === "") {
      return "";
    }

    const cleanValue = String(value).replace(/[^0-9.-]/g, "");
    const numValue = parseFloat(cleanValue);

    if (isNaN(numValue)) {
      return String(value);
    }

    return formatNumber(numValue, decimals, thousandsSeparator);
  }, [value, decimals, thousandsSeparator, isEditing, editingValue]);

  // Handle focus to start editing mode
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditing) {
      // Remove formatting when entering edit mode - show only numbers
      const currentValue = e.target.value;
      const cleanValue = currentValue.replace(/[^0-9.-]/g, "");

      // CRITICAL: Update the input value immediately so Delete works at position 0
      // This must happen synchronously before the user can press Delete
      e.target.value = cleanValue;

      setEditingValue(cleanValue);
      setIsEditing(true);
    }

    if (inputProps.onFocus) {
      inputProps.onFocus(e);
    }
  };

  // Handle keyboard events for proper Delete/Backspace behavior
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isEditing || !inputRef.current) return;

    const input = inputRef.current;
    const cursorPos = input.selectionStart || 0;
    const cursorEnd = input.selectionEnd || 0;
    const currentValue = input.value;

    // Both Delete and Backspace should delete from left to right (forward delete)
    if (
      (e.key === "Delete" || e.key === "Backspace") &&
      cursorPos === cursorEnd
    ) {
      e.preventDefault();

      if (cursorPos < currentValue.length) {
        // Always delete the character to the RIGHT of the cursor
        const newValue =
          currentValue.slice(0, cursorPos) + currentValue.slice(cursorPos + 1);
        input.value = newValue;
        input.setSelectionRange(cursorPos, cursorPos);

        // Trigger change event
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  // React Aria hooks
  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(
      {
        ...otherProps,
        label,
        description,
        isDisabled,
        isRequired,
        type: "text",
        inputMode: "decimal",
        value: displayValue,
        defaultValue,
        onChange: (val) => {
          // Update editing value to preserve what user typed exactly
          if (isEditing) {
            setEditingValue(val);
          }

          // Clean the input value and convert to number
          const cleanValue = val.replace(/[^0-9.-]/g, "");
          const numValue =
            cleanValue === "" || cleanValue === "-"
              ? null
              : parseFloat(cleanValue);

          // Call original onChange with the raw value during editing, cleaned on blur
          if (onChange) {
            onChange(val);
          }

          // Call onValueChange with numeric value
          if (onValueChange) {
            onValueChange(numValue);
          }
        },
        validate: (val) => {
          const cleanValue = val.replace(/[^0-9.-]/g, "");
          const numValue = parseFloat(cleanValue);

          if (isNaN(numValue) && val !== "") {
            return "Please enter a valid number";
          }

          if (min !== undefined && numValue < min) {
            return `Value must be at least ${min}`;
          }

          if (max !== undefined && numValue > max) {
            return `Value must be at most ${max}`;
          }

          return null;
        },
      },
      inputRef,
    );

  // Increment button
  const { buttonProps: incrementButtonProps } = useButton(
    {
      onPress: () => {
        const currentValue = inputRef.current?.value || "0";
        const cleanValue = currentValue.replace(/[^0-9.-]/g, "");
        const numValue = parseFloat(cleanValue) || 0;
        const newValue = numValue + step;

        if (max === undefined || newValue <= max) {
          const formattedValue = formatNumber(
            newValue,
            decimals,
            thousandsSeparator,
          );
          if (inputRef.current) {
            inputRef.current.value = formattedValue;
            inputRef.current.dispatchEvent(
              new Event("input", { bubbles: true }),
            );
          }
        }
      },
      isDisabled,
    },
    incrementRef,
  );

  // Decrement button
  const { buttonProps: decrementButtonProps } = useButton(
    {
      onPress: () => {
        const currentValue = inputRef.current?.value || "0";
        const cleanValue = currentValue.replace(/[^0-9.-]/g, "");
        const numValue = parseFloat(cleanValue) || 0;
        const newValue = numValue - step;

        setIsEditing(false);

        if (min === undefined || newValue >= min) {
          const formattedValue = formatNumber(
            newValue,
            decimals,
            thousandsSeparator,
          );
          if (inputRef.current) {
            inputRef.current.value = formattedValue;
            inputRef.current.dispatchEvent(
              new Event("input", { bubbles: true }),
            );
          }
        }
      },
      isDisabled,
    },
    decrementRef,
  );

  // Format number on blur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);

    const blurValue = e.target.value;
    if (blurValue) {
      const cleanValue = blurValue.replace(/[^0-9.-]/g, "");
      const numValue = parseFloat(cleanValue);

      if (!isNaN(numValue)) {
        // Only call onChange if the value actually changed from the original value
        const originalCleanValue = String(value || "").replace(/[^0-9.-]/g, "");
        if (cleanValue !== originalCleanValue && onChange) {
          onChange(cleanValue);
        }
      }
    }

    // Call original onBlur if provided
    if (inputProps.onBlur) {
      inputProps.onBlur(e);
    }
  };

  const hasError = !!errorMessage;
  const containerClasses = [
    styles.container,
    className,
    isDisabled && styles.disabled,
    hasError && styles.hasError,
  ]
    .filter(Boolean)
    .join(" ");

  // Handle responsive width
  const getContainerStyle = () => {
    if (!width) return undefined;

    // If width is an object (responsive), convert to CSS custom properties and media queries
    if (typeof width === "object") {
      const baseWidth = width.base
        ? typeof width.base === "number"
          ? `${width.base}px`
          : width.base
        : "100%";
      return { width: baseWidth };
    }

    // If width is string or number
    return { width: typeof width === "number" ? `${width}px` : width };
  };

  const containerStyle = getContainerStyle();

  // Generate responsive width classes if needed
  const getResponsiveWidthStyle = () => {
    if (!width || typeof width !== "object") return {};

    const responsiveStyles: React.CSSProperties & Record<string, any> = {};

    // Apply media query styles via inline styles object
    // Note: For true responsive support, you'd want to use CSS classes with media queries
    // This is a simplified inline approach
    if (width.S) {
      responsiveStyles["--width-s"] =
        typeof width.S === "number" ? `${width.S}px` : width.S;
    }
    if (width.M) {
      responsiveStyles["--width-m"] =
        typeof width.M === "number" ? `${width.M}px` : width.M;
    }
    if (width.L) {
      responsiveStyles["--width-l"] =
        typeof width.L === "number" ? `${width.L}px` : width.L;
    }
    if (width.XL) {
      responsiveStyles["--width-xl"] =
        typeof width.XL === "number" ? `${width.XL}px` : width.XL;
    }

    return responsiveStyles;
  };

  const responsiveStyles = getResponsiveWidthStyle();
  const finalContainerStyle = { ...containerStyle, ...responsiveStyles };

  return (
    <div
      className={containerClasses}
      style={finalContainerStyle}
      data-responsive-width={typeof width === "object" ? "true" : undefined}
    >
      {/* Label */}
      {label && (
        <label {...labelProps} className={styles.label}>
          {label}
          {isRequired && (
            <span className={styles.required} aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Input wrapper */}
      <div className={styles.inputWrapper}>
        {/* Prefix (Currency/Text) */}
        {prefix && (
          <span className={styles.prefix} aria-hidden="true">
            {prefix}
          </span>
        )}

        {/* Input field */}
        <input
          {...inputProps}
          ref={inputRef}
          className={styles.input}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />

        {/* Suffix */}
        {suffix && (
          <span className={styles.suffix} aria-hidden="true">
            {suffix}
          </span>
        )}

        {/* Increment/Decrement buttons */}
        {showButtons && (
          <div className={styles.buttonGroup}>
            <button
              {...incrementButtonProps}
              ref={incrementRef}
              className={styles.incrementButton}
              aria-label="Increment value"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 4l4 4H4z" />
              </svg>
            </button>
            <button
              {...decrementButtonProps}
              ref={decrementRef}
              className={styles.decrementButton}
              aria-label="Decrement value"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 12l-4-4h8z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <div {...descriptionProps} className={styles.description}>
          {description}
        </div>
      )}

      {/* Error message */}
      {hasError && (
        <div {...errorMessageProps} className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export default NumericTextField;
